import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useGetCmsTheme } from "@/api/cms";
import { Button } from "@/components/ui/button";
import { CmsPageAppearanceSaveRegistrar } from "@/components/cms/theme/CmsPageAppearanceSaveRegistrar";
import {
  CmsPageAppearanceProvider,
  useCmsPageAppearance,
} from "@/context/cms/CmsPageAppearanceContext";
import { CmsThemeEditorProvider } from "@/context/cms/CmsThemeEditorContext";
import type { CmsColor } from "@/lib/cms/cms-theme.constants";
import { CmsAdminShell } from "@/pages/cms/advanced/AdminShell";

/**
 * Coquille du CMS avancé pour un FORMULAIRE de l'ancien CMS (Authentification,
 * E-shop…) monté À L'INTÉRIEUR de `CmsAdminShell`. Fournit la même pile de
 * providers que `CMSLayout` (apparence de page + éditeur de thème) pour que le
 * `CmsPageAppearanceConfig` de l'éditeur (drawer « Apparence ») fonctionne. Le
 * bouton « Apparence » et le bouton Enregistrer pilotent le `<form id={formId}>`
 * rendu par l'éditeur.
 */
function LegacyFormShellInner({
  title,
  formId,
  children,
}: {
  title: string;
  formId: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { formId: registeredFormId, setOpen } = useCmsPageAppearance();

  const save = () => {
    const form = document.getElementById(formId);
    if (form instanceof HTMLFormElement) form.requestSubmit();
  };

  return (
    <>
      <CmsPageAppearanceSaveRegistrar submitAll={save} />
      <CmsAdminShell
        title={title}
        formIds={[formId]}
        actions={
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={!registeredFormId}
              onClick={() => setOpen(true)}
            >
              {t("cms.page.appearance.open")}
            </Button>
            <Button onClick={save}>{t("cmsEnregistrerBouton")}</Button>
          </div>
        }
      >
        <div className="px-6 py-6">{children}</div>
      </CmsAdminShell>
    </>
  );
}

export function CmsAdvancedLegacyFormShell({
  title,
  formId,
  children,
}: {
  title: string;
  formId: string;
  children: ReactNode;
}) {
  const { data: cmsTheme } = useGetCmsTheme();
  const [colors, setColors] = useState<CmsColor[]>([]);

  useEffect(() => {
    if (cmsTheme?.colors) setColors(cmsTheme.colors);
  }, [cmsTheme?.colors]);

  return (
    <CmsPageAppearanceProvider>
      <CmsThemeEditorProvider colors={colors} onColorsChange={setColors}>
        <LegacyFormShellInner title={title} formId={formId}>
          {children}
        </LegacyFormShellInner>
      </CmsThemeEditorProvider>
    </CmsPageAppearanceProvider>
  );
}
